import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowLeft, X, Check, ArrowUpCircle, ArrowDownCircle, User, Building, Plus } from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { gradientText, gradientButton } from '../theme';
import ImageCropper from '../components/ImageCropper';
import CountrySelect from '../components/CountrySelect';
import 'react-phone-input-2/lib/style.css';
import '../styles/phoneInput.css';
import { supabase } from '../lib/supabase';

// Interface pour les données de contact
interface ContactInfo {
  phone: string;
  contactLink: string;
}

function SignUpFlow() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { updateProfile } = useProfile();
  
  // État pour suivre l'étape actuelle
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // État pour les données du formulaire
  const [formData, setFormData] = useState({
    // Phase 1: Informations de base
    fullName: '',
    country: '',
    city: '',
    language: i18n.language || 'fr', // Langue par défaut
    
    // Phase 2: Profil personnel
    gender: '',
    age: '', // Âge au lieu de date de naissance
    sexuality: '',
    
    // Phase 3: Type de relation
    relationType: '',
    shortTermIntentions: {
      money: '',
      moneyFlow: 'give',
      hookup: false,
      resto: false,
      drink: false,
      seeWhat: false,
      other: false
    },
    longTermIntentions: {
      marriage: false,
      children: false,
      fidelity: false,
      polygamy: false,
      free: false
    },
    
    // Phase 4: Contact
    contactInfo: {
      phone: '',
      contactLink: ''
    } as ContactInfo,
    
    // Phase 5: Photos
    photos: [] as string[],
    
    // Phase 6: Visibilité et parrainage
    isVisible: true,
    referralCode: ''
  });
  
  // État pour le recadrage d'image
  const [showCropper, setShowCropper] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number | null>(null);
  
  // Vérifier si l'utilisateur est connecté
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);
  
  // Vérifier si l'utilisateur vient d'un lien de partage ou a certifié/rejeté une relation
  useEffect(() => {
    const pendingRelationId = localStorage.getItem('pendingRelationId');
    const certifiedRelationId = localStorage.getItem('certified_relation_id');
    
    // Si l'utilisateur a une relation en attente, cela a priorité
    if (pendingRelationId) {
      // Rediriger vers la page de partage
      navigate(`/share/${pendingRelationId}`);
    } else if (certifiedRelationId) {
      // L'utilisateur a déjà certifié/rejeté une relation et est en train de compléter son profil
      // On peut nettoyer cette information car elle n'est plus utile pour la redirection
      localStorage.removeItem('certified_relation_id');
      // On laisse l'utilisateur compléter son profil normalement
    }
  }, [navigate]);
  
  // Récupérer le code de parrainage s'il existe
  useEffect(() => {
    const storedReferralCode = localStorage.getItem('referral_code');
    if (storedReferralCode) {
      console.log("[SignUpFlow] Found referral code in localStorage:", storedReferralCode);
      setFormData(prev => ({
        ...prev,
        referralCode: storedReferralCode
      }));
    }
  }, []);
  
  // Fonction pour mettre à jour les données du formulaire
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | string, fieldName?: string) => {
    if (typeof e === 'string' && fieldName) {
      // Cas spécial pour CountrySelect
      setFormData(prev => ({
        ...prev,
        [fieldName]: e
      }));
    } else if (typeof e === 'object') {
      // Cas normal pour les autres inputs
      const { name, value, type } = e.target;
      
      if (type === 'checkbox') {
        const checkbox = e.target as HTMLInputElement;
        const [section, field] = name.split('.');
        
        if (section === 'shortTermIntentions' || section === 'longTermIntentions') {
          setFormData(prev => {
            if (section === 'shortTermIntentions') {
              return {
                ...prev,
                shortTermIntentions: {
                  ...prev.shortTermIntentions,
                  [field]: checkbox.checked
                }
              };
            } else {
              return {
                ...prev,
                longTermIntentions: {
                  ...prev.longTermIntentions,
                  [field]: checkbox.checked
                }
              };
            }
          });
        }
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };
  
  // Fonction pour gérer le changement de genre
  const handleGenderChange = (gender: string) => {
    setFormData(prev => ({
      ...prev,
      gender
    }));
  };
  
  // Fonction pour gérer le changement d'orientation sexuelle
  const handleSexualityChange = (sexuality: string) => {
    setFormData(prev => ({
      ...prev,
      sexuality
    }));
  };
  
  // Fonction pour gérer le changement de type de relation
  const handleRelationTypeChange = (type: string) => {
    setFormData(prev => ({
      ...prev,
      relationType: type
    }));
    
    // Si on passe en mode "relation longue", on efface le montant d'argent
    if (type === 'long') {
      setFormData(prev => ({
        ...prev,
        shortTermIntentions: {
          ...prev.shortTermIntentions,
          money: ''
        }
      }));
    }
  };
  
  // Fonction pour gérer le changement d'intentions à court terme
  const handleShortTermIntentionsChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      shortTermIntentions: {
        ...prev.shortTermIntentions,
        [field]: value
      }
    }));
  };
  
  // Fonction pour gérer le changement d'intentions à long terme
  const handleLongTermIntentionsChange = (field: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      longTermIntentions: {
        ...prev.longTermIntentions,
        [field]: value
      }
    }));
  };
  
  // Fonction pour gérer le changement d'informations de contact
  const handleContactInfoChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [field]: value
      }
    }));
  };
  
  // Fonction pour gérer la sélection d'une photo
  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Créer une URL pour l'aperçu
    const imageUrl = URL.createObjectURL(file);
    setCurrentImageUrl(imageUrl);
    setCurrentPhotoIndex(index);
    setShowCropper(true);
  };
  
  // Fonction pour gérer la suppression d'une photo
  const handleDeletePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };
  
  // Fonction pour gérer la fin du recadrage
  const handleCropComplete = async (croppedImageUrl: string) => {
    setShowCropper(false);
    
    if (currentPhotoIndex !== null) {
      // Mettre à jour la photo à l'index spécifié
      const updatedPhotos = [...formData.photos];
      updatedPhotos[currentPhotoIndex] = croppedImageUrl;
      
      setFormData(prev => ({
        ...prev,
        photos: updatedPhotos
      }));
    }
  };
  
  // Fonction pour gérer le changement de visibilité
  const handleVisibilityChange = (visible: boolean) => {
    setFormData(prev => ({
      ...prev,
      isVisible: visible
    }));
  };
  
  // Fonction pour passer à l'étape suivante
  const nextStep = () => {
    if (currentStep < 6) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  // Fonction pour revenir à l'étape précédente
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // Fonction pour valider l'étape actuelle
  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return formData.fullName && formData.country && formData.city;
      case 2:
        return formData.gender !== '' && formData.sexuality !== '' && formData.age !== '';
      case 3:
        return formData.relationType !== '';
      case 4:
        return formData.contactInfo.contactLink;
      case 5:
        return formData.photos.length >= 2;
      case 6:
        return true; // La visibilité est toujours valide
      default:
        return false;
    }
  };
  
  // Fonction pour sélectionner une photo aléatoire comme avatar
  const selectRandomPhotoAsAvatar = (photos: string[]) => {
    // Filtrer les photos non vides
    const validPhotos = photos.filter(photo => photo !== '');
    if (validPhotos.length === 0) return null;
    
    // Sélectionner une photo au hasard
    const randomIndex = Math.floor(Math.random() * validPhotos.length);
    return validPhotos[randomIndex];
  };
  
  // Fonction pour sauvegarder le profil
  const handleSaveProfile = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Créer les tableaux d'intentions pour correspondre au schéma de la base de données
      const shortTermIntentionsArray = [];
      const longTermIntentionsArray = [];
      
      // Convertir les intentions à court terme en tableau
      if (formData.shortTermIntentions.moneyFlow) shortTermIntentionsArray.push(`moneyFlow:${formData.shortTermIntentions.moneyFlow}`);
      if (formData.shortTermIntentions.hookup) shortTermIntentionsArray.push('hookup');
      if (formData.shortTermIntentions.resto) shortTermIntentionsArray.push('resto');
      if (formData.shortTermIntentions.drink) shortTermIntentionsArray.push('drink');
      if (formData.shortTermIntentions.seeWhat) shortTermIntentionsArray.push('seeWhat');
      if (formData.shortTermIntentions.other) shortTermIntentionsArray.push('other');
      
      // Convertir les intentions à long terme en tableau
      if (formData.longTermIntentions.marriage) longTermIntentionsArray.push('marriage');
      if (formData.longTermIntentions.children) longTermIntentionsArray.push('children');
      if (formData.longTermIntentions.fidelity) longTermIntentionsArray.push('fidelity');
      if (formData.longTermIntentions.polygamy) longTermIntentionsArray.push('polygamy');
      if (formData.longTermIntentions.free) longTermIntentionsArray.push('free');

      // Sélectionner une photo aléatoire comme avatar si aucun avatar n'est défini
      const randomAvatar = selectRandomPhotoAsAvatar(formData.photos);
      
      // Mettre à jour le profil avec les données du formulaire
      const { error: updateError } = await updateProfile({
        full_name: formData.fullName,
        country: formData.country,
        city: formData.city,
        gender: formData.gender,
        age: formData.age,
        sexual_orientation: formData.sexuality,
        relation_type: formData.relationType,
        short_term_intentions: shortTermIntentionsArray,
        long_term_intentions: longTermIntentionsArray,
        is_visible: formData.isVisible,
        photos: formData.photos,
        custom_link_url: formData.contactInfo.contactLink,
        money_amount: formData.shortTermIntentions.money,
        avatar_url: randomAvatar || null, // Utiliser la photo aléatoire comme avatar
      });
      
      if (updateError) throw updateError;
      
      // Marquer localement que l'utilisateur n'est plus nouveau
      localStorage.setItem('isNewUser', 'false');
      
      // Mettre à jour le code de parrainage séparément si nécessaire
      if (formData.referralCode) {
        console.log("[SignUpFlow] Updating profile with referral code:", formData.referralCode);
        
        try {
          // Étape 1: Mettre à jour le profil avec le code de parrainage
          const { error: refError } = await supabase
            .from('profiles')
            .update({ referral_code: formData.referralCode })
            .eq('id', user.id);
            
          if (refError) {
            console.error("[SignUpFlow] Error updating profile with referral code:", refError);
          } else {
            console.log("[SignUpFlow] Profile updated with referral code");
            
            // Étape 2: Appliquer le code de parrainage
            try {
              console.log("[SignUpFlow] Applying referral code:", formData.referralCode);
              const { data, error } = await supabase.rpc('apply_referral_code', {
                user_id: user.id,
                code: formData.referralCode
              });
              
              if (error) {
                console.error("[SignUpFlow] Error applying referral code:", error);
                // Si la fonction RPC échoue, essayer avec une mise à jour manuelle
                try {
                  // Vérifier si le code existe dans admin_users
                  const { data: adminData, error: adminError } = await supabase
                    .from('admin_users')
                    .select('user_id')
                    .eq('referral_code', formData.referralCode)
                    .single();
                  
                  if (!adminError && adminData) {
                    // Méthode alternative 1: Créer directement une entrée dans referrals
                    const { error: referralError } = await supabase
                      .from('referrals')
                      .insert({
                        admin_id: adminData.user_id,  // Utiliser admin_id (au lieu de referrer_id)
                        user_id: user.id,            // Utiliser user_id (au lieu de referred_id)
                        status: 'pending',
                        commission_rate: 10.00       // Utiliser 10.00 au lieu de 0.10
                      });
                      
                    if (referralError) {
                      console.error("[SignUpFlow] Fallback 1 error - direct referral entry:", referralError);
                      
                      // Méthode alternative 2: Mettre à jour uniquement le profil
                      const { error: updateError } = await supabase
                        .from('profiles')
                        .update({ referred_by: adminData.user_id })
                        .eq('id', user.id);
                        
                      if (updateError) {
                        console.error("[SignUpFlow] Fallback 2 error - profile update:", updateError);
                      } else {
                        console.log("[SignUpFlow] Successfully updated referred_by field manually");
                      }
                    } else {
                      console.log("[SignUpFlow] Successfully created referral entry manually");
                      
                      // Mettre également à jour le profil pour cohérence
                      const { error: updateError } = await supabase
                        .from('profiles')
                        .update({ referred_by: adminData.user_id })
                        .eq('id', user.id);
                        
                      if (updateError) {
                        console.error("[SignUpFlow] Error updating referred_by after manual referral:", updateError);
                      }
                    }
                  } else {
                    console.error("[SignUpFlow] Could not find admin with referral code:", adminError);
                  }
                } catch (err) {
                  console.error("[SignUpFlow] Error in fallback referral process:", err);
                }
              } else {
                console.log("[SignUpFlow] Referral code applied successfully:", data);
              }
            } catch (err) {
              console.error("[SignUpFlow] Exception applying referral code:", err);
            }
          }
        } catch (err) {
          console.error("[SignUpFlow] Exception updating profile with referral code:", err);
        }
      }
      
      // Récupérer le code de parrainage depuis localStorage (pour rétrocompatibilité)
      const storedReferralCode = localStorage.getItem('referral_code');
      if (storedReferralCode && !formData.referralCode) {
        console.log("[SignUpFlow] Found referral code in localStorage:", storedReferralCode);
        
        try {
          // Mettre à jour le profil avec le code de parrainage stocké
          const { error: refError } = await supabase
            .from('profiles')
            .update({ referral_code: storedReferralCode })
            .eq('id', user.id);
            
          if (refError) {
            console.error("[SignUpFlow] Error updating profile with stored referral code:", refError);
          } else {
            console.log("[SignUpFlow] Profile updated with stored referral code");
            
            // Appliquer le code de parrainage
            const { data, error } = await supabase.rpc('apply_referral_code', {
              user_id: user.id,
              code: storedReferralCode
            });
            
            if (error) {
              console.error("[SignUpFlow] Error applying stored referral code:", error);
            } else {
              console.log("[SignUpFlow] Stored referral code applied successfully:", data);
              // Nettoyer le localStorage après application réussie
              localStorage.removeItem('referral_code');
            }
          }
        } catch (err) {
          console.error("[SignUpFlow] Exception with stored referral code:", err);
        }
      }
      
      // Rediriger vers le profil
      navigate('/profile');
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(t('signupFlow.error.savingProfile'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fonction pour changer la langue
  const handleLanguageChange = async (lang: string) => {
    // Changer la langue dans i18n
    await i18n.changeLanguage(lang);
    
    // Mettre à jour le state
    setFormData(prev => ({
      ...prev,
      language: lang
    }));
  };
  
  // Rendu du composant de recadrage d'image
  if (showCropper) {
    return (
      <ImageCropper
        imageUrl={currentImageUrl}
        onCropComplete={handleCropComplete}
        onCancel={() => setShowCropper(false)}
        aspectRatio={1}
      />
    );
  }
  
  // Rendu de l'étape actuelle
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <div className="grid grid-cols-2 gap-3">
                {['fr', 'en'].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => handleLanguageChange(lang)}
                    className={`p-4 rounded-xl border ${
                      formData.language === lang
                        ? 'border-primary-end bg-primary-end/10'
                        : 'border-gray-800 hover:border-gray-700'
                    } text-white text-sm font-medium transition-colors`}
                  >
                    {t(`signupFlow.step1.languageOptions.${lang}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full bg-black border border-gray-800 rounded-xl pl-12 pr-4 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm"
                  placeholder={t('signupFlow.step1.fullNamePlaceholder')}
                />
              </div>
            </div>
            
            <div>
              <CountrySelect
                value={formData.country}
                onChange={(value) => handleInputChange(value, 'country')}
                placeholder={t('signupFlow.step1.countryPlaceholder')}
              />
            </div>
            
            <div>
              <div className="relative">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full bg-black border border-gray-800 rounded-xl pl-12 pr-4 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm"
                  placeholder={t('signupFlow.step1.cityPlaceholder')}
                />
              </div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <div className="grid grid-cols-2 gap-3">
                {['homme', 'femme'].map((gender) => (
                  <button
                    key={gender}
                    type="button"
                    onClick={() => handleGenderChange(gender)}
                    className={`p-4 rounded-xl border ${
                      formData.gender === gender
                        ? 'border-primary-end bg-primary-end/10'
                        : 'border-gray-800 hover:border-gray-700'
                    } text-white text-sm font-medium transition-colors`}
                  >
                    {t(`signupFlow.step2.genderOptions.${gender === 'homme' ? 'man' : 'woman'}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                placeholder={t('profile.datingProfile.agePlaceholder')}
                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm"
              />
            </div>

            <div>
              <select
                name="sexuality"
                value={formData.sexuality}
                onChange={(e) => handleSexualityChange(e.target.value)}
                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-end text-white text-sm appearance-none"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center", backgroundSize: "1.5em 1.5em" }}
              >
                <option value="" disabled selected>{t('signupFlow.step2.sexuality')}</option>
                <option value="hetero">{t('signupFlow.step2.sexualityOptions.hetero')}</option>
                <option value="gay">{t('signupFlow.step2.sexualityOptions.gay')}</option>
                <option value="lesbian">{t('signupFlow.step2.sexualityOptions.lesbian')}</option>
                <option value="bi">{t('signupFlow.step2.sexualityOptions.bi')}</option>
              </select>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-base font-semibold text-white mb-3">
                {t('signupFlow.step3.youSearch')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['court', 'long'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleRelationTypeChange(type)}
                    className={`p-4 rounded-xl border ${
                      formData.relationType === type
                        ? 'border-primary-end bg-primary-end/10'
                        : 'border-gray-800 hover:border-gray-700'
                    } text-white text-sm font-medium transition-colors`}
                  >
                    {t(`signupFlow.step3.${type === 'court' ? 'shortTerm' : 'longTerm'}`)}
                  </button>
                ))}
              </div>
            </div>
            
            {formData.relationType === 'court' && (
              <div className="space-y-4">
                <h4 className="text-base font-medium text-gray-300">{t('signupFlow.step3.shortTermIntentions')}</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    {t('signupFlow.step3.money')}
                  </label>
                  <input
                    type="number"
                    name="money"
                    value={formData.shortTermIntentions.money}
                    onChange={(e) => handleShortTermIntentionsChange('money', e.target.value)}
                    className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm"
                    placeholder={t('signupFlow.step3.moneyPlaceholder')}
                  />
                </div>
                
                {/* Sélecteur Donner/Recevoir */}
                <div className="flex space-x-2">
                  <label className={`flex-1 flex items-center justify-center gap-2 rounded-lg border transition-colors px-3 py-2 cursor-pointer ${
                    formData.shortTermIntentions.moneyFlow === 'pay' 
                      ? 'border-green-500 bg-black/50 text-green-500'
                      : 'border-gray-700 bg-black text-gray-400 hover:border-gray-600'
                  }`}>
                    <input
                      type="radio"
                      name="moneyFlow"
                      value="pay"
                      className="sr-only"
                      checked={formData.shortTermIntentions.moneyFlow === 'pay'}
                      onChange={() => handleShortTermIntentionsChange('moneyFlow', 'pay')}
                    />
                    <ArrowUpCircle className={`h-5 w-5 ${formData.shortTermIntentions.moneyFlow === 'pay' ? 'animate-float' : ''}`} />
                    <span className="text-sm font-medium">{t('signupFlow.step3.iPay')}</span>
                  </label>
                  
                  <label className={`flex-1 flex items-center justify-center gap-2 rounded-lg border transition-colors px-3 py-2 cursor-pointer ${
                    formData.shortTermIntentions.moneyFlow === 'receive' 
                      ? 'border-primary-end bg-black/50 text-primary-end'
                      : 'border-gray-700 bg-black text-gray-400 hover:border-gray-600'
                  }`}>
                    <input
                      type="radio"
                      name="moneyFlow"
                      value="receive"
                      className="sr-only"
                      checked={formData.shortTermIntentions.moneyFlow === 'receive'}
                      onChange={() => handleShortTermIntentionsChange('moneyFlow', 'receive')}
                    />
                    <ArrowDownCircle className={`h-5 w-5 ${formData.shortTermIntentions.moneyFlow === 'receive' ? 'animate-float' : ''}`} />
                    <span className="text-sm font-medium">{t('signupFlow.step3.iReceive')}</span>
                  </label>
                </div>
                
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-1">
                    {['hookup', 'resto', 'drink', 'seeWhat', 'other'].map((intention) => (
                      <label key={intention} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          name={`shortTermIntentions.${intention}`}
                          checked={formData.shortTermIntentions[intention as keyof typeof formData.shortTermIntentions] as boolean}
                          onChange={(e) => handleShortTermIntentionsChange(intention, e.target.checked)}
                          className="form-checkbox text-primary-end focus:ring-primary-end focus:ring-offset-black"
                        />
                        <span className="text-sm text-white">
                          {t(`signupFlow.step3.${intention}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {formData.relationType === 'long' && (
              <div className="space-y-4">
                <h4 className="text-base font-medium text-gray-300">{t('signupFlow.step3.longTermIntentions')}</h4>
                
                <div className="grid grid-cols-2 gap-1">
                  {['marriage', 'children', 'fidelity', 'polygamy', 'free'].map((intention) => (
                    <label key={intention} className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        name={`longTermIntentions.${intention}`}
                        checked={formData.longTermIntentions[intention as keyof typeof formData.longTermIntentions]}
                        onChange={(e) => handleLongTermIntentionsChange(intention, e.target.checked)}
                        className="form-checkbox text-primary-end focus:ring-primary-end focus:ring-offset-black"
                      />
                      <span className="text-sm text-white">
                        {t(`signupFlow.step3.${intention}`)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-4">
            <div className="bg-[#0A0A0A] rounded-xl p-4">
              <div className="text-sm text-gray-400 mb-1">{t('settings.link.title')}</div>
              <input
                type="text"
                name="contactLink"
                value={formData.contactInfo.contactLink}
                onChange={(e) => handleContactInfoChange('contactLink', e.target.value)}
                onBlur={(e) => {
                  let value = e.target.value.trim();
                  
                  // Vérifier si la valeur est un numéro de téléphone (avec différents formats possibles)
                  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;
                  
                  if (phoneRegex.test(value) && !value.startsWith('tel:')) {
                    // Supprimer tous les caractères non numériques sauf le + au début
                    const cleanedPhone = value.startsWith('+') 
                      ? '+' + value.substring(1).replace(/\D/g, '') 
                      : value.replace(/\D/g, '');
                    
                    // Convertir en format de lien téléphonique
                    value = `tel:${cleanedPhone}`;
                    
                    // Mettre à jour manuellement la valeur affichée
                    e.target.value = value;
                  } else if (value && !value.startsWith('http://') && !value.startsWith('https://') && !value.startsWith('tel:')) {
                    // Si c'est un lien web sans protocol, ajouter https://
                    value = `https://${value}`;
                    
                    // Mettre à jour manuellement la valeur affichée
                    e.target.value = value;
                  }
                  
                  // Mettre à jour l'état avec la valeur formatée
                  handleContactInfoChange('contactLink', value);
                }}
                className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-primary-end"
                placeholder="https://... ou numéro de téléphone"
              />
              <div className="text-xs text-gray-500 mt-1">
                {t('settings.link.hint')}
              </div>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-400 mb-4">
              {t('signupFlow.step5.description')}
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="relative aspect-square">
                  {formData.photos[index] ? (
                    <div className="relative w-full h-full">
                      <img
                        src={formData.photos[index]}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover rounded-xl"
                      />
                      <input
                        id={`photo-${index}`}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoSelect(e, index)}
                      />
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(index)}
                        className="absolute top-2 right-2 bg-white rounded-full p-1"
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-full border-2 border-dashed border-gray-800 rounded-xl cursor-pointer hover:border-gray-700 transition-colors">
                      <input
                        id={`photo-${index}`}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoSelect(e, index)}
                      />
                      <button
                        type="button"
                        onClick={() => document.getElementById(`photo-${index}`)?.click()}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <Plus className="h-6 w-6" />
                      </button>
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
        
      case 6:
        return (
          <div className="space-y-4">
            {/* Bouton ON/OFF */}
            <div className="bg-[#0A0A0A] rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-white">{t('profile.datingProfile.activateVisibility')}</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={formData.isVisible} 
                    onChange={(e) => handleVisibilityChange(e.target.checked)} 
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-primary-start peer-checked:to-primary-end"></div>
                </label>
              </div>
              
              {/* Information sur la désactivation automatique */}
              <div className="mt-2 border-t border-gray-800 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">{t('profile.datingProfile.contactClicks')}</span>
                  <span className="text-white font-medium">0/5</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('profile.datingProfile.contactClicksInfo')}
                </p>
              </div>
            </div>

            {/* Code de parrainage */}
            <div className="bg-[#0A0A0A] rounded-xl p-3">
              <div className="text-sm text-gray-400 mb-1">{t('profile.referral.code')}</div>
              <input
                type="text"
                name="referralCode"
                value={formData.referralCode}
                onChange={(e) => setFormData(prev => ({...prev, referralCode: e.target.value}))}
                readOnly={!!localStorage.getItem('referral_code')}
                className={`w-full bg-black border ${localStorage.getItem('referral_code') ? 'border-primary-end bg-black/50' : 'border-gray-800'} rounded-lg px-3 py-2 text-white focus:outline-none focus:border-primary-end`}
                placeholder={t('profile.referral.codePlaceholder')}
              />
              {localStorage.getItem('referral_code') ? (
                <p className="text-xs text-primary-end mt-1">
                  {t('profile.referral.codeApplied')}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {t('profile.referral.codeInfo')}
                </p>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-background-primary py-12 px-4 sm:px-6 lg:px-8">
      {/* Styles personnalisés pour les cases à cocher */}
      <style>
        {`
          .form-checkbox:checked {
            background-image: linear-gradient(to right, #B8A44E, #ec4899);
            border-color: transparent;
          }
          .form-checkbox:focus {
            box-shadow: 0 0 0 2px rgba(184, 164, 78, 0.3);
            border-color: transparent;
          }
          .step-card {
            background: rgba(0, 0, 0, 0.7);
            border-radius: 1rem;
            border: 1px solid rgba(75, 85, 99, 0.4);
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
            position: relative;
            overflow: hidden;
          }
          .step-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(184, 164, 78, 0.5), rgba(236, 72, 153, 0.5), transparent);
          }
          .step-card::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(236, 72, 153, 0.3), rgba(184, 164, 78, 0.3), transparent);
          }
          .step-header {
            border-bottom: 1px solid rgba(75, 85, 99, 0.3);
            position: relative;
            overflow: hidden;
          }
          .step-header::after {
            content: '';
            position: absolute;
            bottom: -1px;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(184, 164, 78, 0.3), rgba(236, 72, 153, 0.3), transparent);
            animation: shimmer 2s infinite;
          }
          @keyframes shimmer {
            0% { opacity: 0.3; }
            50% { opacity: 0.7; }
            100% { opacity: 0.3; }
          }
          .outer-frame {
            border: 1px solid rgba(75, 85, 99, 0.6);
            background: rgba(0, 0, 0, 0.2);
            border-radius: 1.5rem;
            position: relative;
            overflow: hidden;
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
          }
          .outer-frame::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, 
              rgba(0, 0, 0, 0), 
              rgba(184, 164, 78, 0.8), 
              rgba(236, 72, 153, 0.8), 
              rgba(0, 0, 0, 0));
            animation: shimmer 3s infinite;
          }
          .outer-frame::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, 
              rgba(0, 0, 0, 0), 
              rgba(236, 72, 153, 0.8), 
              rgba(184, 164, 78, 0.8), 
              rgba(0, 0, 0, 0));
            animation: shimmer 3s infinite reverse;
          }
          .side-border-left::before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 2px;
            background: linear-gradient(to bottom, 
              rgba(0, 0, 0, 0), 
              rgba(184, 164, 78, 0.8), 
              rgba(236, 72, 153, 0.8), 
              rgba(0, 0, 0, 0));
            animation: shimmer-vertical 4s infinite;
          }
          .side-border-right::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            right: 0;
            width: 2px;
            background: linear-gradient(to bottom, 
              rgba(0, 0, 0, 0), 
              rgba(236, 72, 153, 0.8), 
              rgba(184, 164, 78, 0.8), 
              rgba(0, 0, 0, 0));
            animation: shimmer-vertical 4s infinite reverse;
          }
          @keyframes shimmer-vertical {
            0% { opacity: 0.4; }
            50% { opacity: 0.9; }
            100% { opacity: 0.4; }
          }
          .border-gradient-circle {
            position: relative;
            background-color: transparent;
            border: none;
          }
          .border-gradient-circle::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            border-radius: 50%;
            padding: 2px;
            background: linear-gradient(to right, #B8A44E, #ec4899);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            pointer-events: none;
          }
          .step-circle-active {
            color: white;
            font-weight: 500;
            background-color: transparent;
            box-shadow: 0 0 10px rgba(236, 72, 153, 0.3);
          }
          .step-circle-active::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 50%;
            border: 2px solid transparent;
            background: linear-gradient(to right, #B8A44E, #ec4899) border-box;
            -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: destination-out;
            mask-composite: exclude;
            animation: circle-glow 2s infinite ease-in-out;
          }
          @keyframes circle-glow {
            0% { opacity: 0.8; }
            50% { opacity: 1; box-shadow: 0 0 12px rgba(236, 72, 153, 0.5); }
            100% { opacity: 0.8; }
          }
          .progress-gradient {
            background: linear-gradient(to right, #B8A44E, #ec4899);
            box-shadow: 0 0 8px rgba(236, 72, 153, 0.4);
            position: relative;
            overflow: hidden;
          }
          .progress-gradient::after {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 50%;
            height: 100%;
            background: linear-gradient(90deg, 
              rgba(255, 255, 255, 0), 
              rgba(255, 255, 255, 0.15), 
              rgba(255, 255, 255, 0)
            );
            animation: progress-shine 2s infinite;
          }
          @keyframes progress-shine {
            0% { left: -100%; }
            100% { left: 200%; }
          }
        `}
      </style>
      <div className="max-w-3xl mx-auto">
        {/* Cadre extérieur qui englobe tout */}
        <div className="outer-frame side-border-left side-border-right p-px">
          {/* Conteneur principal avec effet de verre */}
          <div className="backdrop-blur-xl bg-black/40 border border-gray-800/50 shadow-xl rounded-2xl overflow-hidden">
            {/* En-tête avec dégradé */}
            <div className="bg-gradient-to-r from-gray-900/50 to-black/50 border-b border-gray-800/50 px-6 py-4 step-header">
              <h2 className={`text-2xl font-bold mb-1 ${gradientText}`}>
                {t('signupFlow.title')}
              </h2>
              <p className="text-gray-400">
                {t(`signupFlow.stepSubtitles.${currentStep}`) || t('signupFlow.subtitle')}
              </p>
            </div>

            <div className="px-6 py-4">
              {/* Progress bar */}
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  {[1, 2, 3, 4, 5, 6].map((step) => (
                    <div
                      key={step}
                      className={`relative w-8 h-8 rounded-full flex items-center justify-center ${
                        currentStep >= step 
                          ? 'step-circle-active'
                          : 'border border-gray-700 text-gray-500 bg-transparent hover:border-gray-600'
                      } transition-all duration-300`}
                    >
                      {step}
                    </div>
                  ))}
                </div>
                <div className="h-1 w-11/12 mx-auto bg-gray-800/20 rounded-full overflow-hidden mb-1">
                  <div
                    className="h-1 rounded-full transition-all duration-500 progress-gradient"
                    style={{ width: `${(currentStep / 6) * 100}%` }}
                  />
                </div>
              </div>

              {/* Step title in its own card */}
              <div className="step-card mb-4">
                <div className="step-header px-5 py-3">
                  <h3 className={`text-base font-medium ${gradientText}`}>
                    {t(`signupFlow.step${currentStep}.title`)}
                  </h3>
                </div>
              </div>

              {/* Contenu de l'étape directement sans cadre supplémentaire */}
              {renderStep()}

              {/* Navigation buttons */}
              <div className="mt-8 flex justify-between">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="inline-flex items-center px-6 py-2.5 border border-gray-800/50 text-sm font-medium rounded-xl text-white bg-black/40 hover:bg-black/60 transition-colors backdrop-blur-xl"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('signupFlow.prevStep')}
                  </button>
                ) : (
                  <div></div>
                )}

                {currentStep < 6 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!validateStep()}
                    className={`inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-xl text-white transition-all duration-200 ${
                      validateStep() 
                        ? `${gradientButton} hover:shadow-lg hover:shadow-primary-end/20`
                        : 'bg-gray-800/50 cursor-not-allowed'
                    }`}
                  >
                    {t('signupFlow.nextStep')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSaveProfile}
                    disabled={isLoading || !validateStep()}
                    className={`inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-xl text-white transition-all duration-200 ${
                      !isLoading && validateStep() 
                        ? `${gradientButton} hover:shadow-lg hover:shadow-primary-end/20`
                        : 'bg-gray-800/50 cursor-not-allowed'
                    }`}
                  >
                    {isLoading ? t('common.saving') : t('signupFlow.finish')}
                    {!isLoading && <Check className="ml-2 h-4 w-4" />}
                  </button>
                )}
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-900/20 text-red-400 rounded-xl border border-red-800/50 backdrop-blur-sm">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpFlow;